import type { Action, ActionComment, KpiArea } from '@/config/types/Action'

export interface ApiAction {
  id: number
  title: string
  due_date: string
  state: 'pending' | 'active' | 'complete'
  color_hex?: string | null
  kpi_area?: string | null
  details?: string | null
  is_private?: boolean
  attachments?: ApiAttachment[]
  comments?: ApiComment[]
  retailer_statuses?: Array<{
    id?: number
    retailer_id: number
    state: 'pending' | 'active' | 'complete'
  }>
}

export interface ApiAttachment {
  id: number
  original_name: string
  stored_path: string
  size_bytes: number
  mime_type: string
  created_at: string
}

export interface ApiComment {
  id: number
  user_id: number
  body: string
  created_at: string
  user?: {
    id: number
    username: string
    forename: string
    surname: string
  }
  replies?: ApiComment[]
}

const toKpiArea = (value?: string | null): KpiArea | undefined => {
  return value && typeof value === 'string' ? (value as KpiArea) : undefined
}

export function mapCommentFromApi(comment: ApiComment): ActionComment {
  return {
    id: comment.id,
    userId: comment.user_id,
    body: comment.body,
    createdAt: comment.created_at,
    user: comment.user
      ? {
          id: comment.user.id,
          username: comment.user.username,
          forename: comment.user.forename,
          surname: comment.user.surname,
        }
      : undefined,
    replies: comment.replies ? comment.replies.map(mapCommentFromApi) : [],
  }
}

export function mapAttachmentFromApi(att: ApiAttachment) {
  return {
    id: att.id,
    originalName: att.original_name,
    storedPath: att.stored_path,
    sizeBytes: att.size_bytes,
    mimeType: att.mime_type,
    createdAt: att.created_at,
  }
}

export function mapActionFromApi(apiAction: ApiAction, includeRelations = true): Action {
  const action: Action = {
    id: apiAction.id,
    title: apiAction.title,
    dueDate: apiAction.due_date,
    state: apiAction.state,
    colorHex: apiAction.color_hex || undefined,
    kpiArea: toKpiArea(apiAction.kpi_area),
    details: apiAction.details || undefined,
    isPrivate: apiAction.is_private ?? undefined,
  }

  if (includeRelations) {
    if (apiAction.attachments) {
      action.attachments = apiAction.attachments.map(mapAttachmentFromApi)
    }

    if (apiAction.comments) {
      action.comments = apiAction.comments.map(mapCommentFromApi)
    }

    if (apiAction.retailer_statuses && Array.isArray(apiAction.retailer_statuses)) {
      action.retailerStatuses = apiAction.retailer_statuses.map((rs) => ({
        retailerId: Number(rs.retailer_id),
        state: rs.state,
      }))
    } else {
      action.retailerStatuses = undefined
    }
  }

  return action
}

export function mapActionToApi(action: Action): Omit<ApiAction, 'id' | 'attachments' | 'comments'> {
  return {
    title: action.title,
    due_date: action.dueDate,
    state: action.state,
    color_hex: action.colorHex || null,
    kpi_area: action.kpiArea || null,
    details: action.details || null,
    is_private: action.isPrivate ?? false,
  }
}
